import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '@/lib/utils';
import {
  MarkedRangeCalendar,
  type MarkerKind,
} from './MarkedRangeCalendar';
import { QuickSelectRange } from './QuickSelectRange';
import { useActivityFeed } from '@/hooks/queries';
import { useQuickSelectItems } from '@/hooks/useQuickSelectItems';
import type { ActivityFeedRow } from '@/api/activityFeed';
import type { DateRangePreset, UseDateRangeReturn } from '@/hooks/useDateRange';

/**
 * Page-level date-range picker — preset chips + last-meal chip + popover
 * calendar + quick-select dropdown. Composes three smaller pieces:
 *
 *   • PRESETS         — fixed "Last 7d / 30d / 90d / This month" chips
 *                       driven by the `useDateRange` preset enum.
 *   • Last-meal chip  — visible badge showing the patient's most recent
 *                       meal date. Click → snap the end of the range to
 *                       that date while preserving the current span.
 *   • Calendar        — `MarkedRangeCalendar` in a popover, with activity
 *                       dots overlayed.
 *   • Quick select    — `QuickSelectRange` dropdown with four grouped
 *                       lists (latest data, tracking periods, past
 *                       reports, CGM devices).
 *
 * Pass `patientId` to enable the last-meal chip and quick-select dropdown.
 * Without it (e.g. on a clinician-level page with no patient context),
 * the picker degrades cleanly to just chips + calendar.
 */
type Props = {
  value: UseDateRangeReturn;
  /** Days to decorate with colored dots, keyed by marker type. */
  markers?: Partial<Record<MarkerKind, Date[]>>;
  /** Required for the last-meal chip + quick-select dropdown. Omit on
   *  non-patient routes — those still get the basic preset chips. */
  patientId?: number | null;
};

const PRESETS: Array<{ value: DateRangePreset; short: string; labelKey: string }> = [
  { value: '7d', short: '7D', labelKey: 'Last 7 days' },
  { value: '30d', short: '30D', labelKey: 'Last 30 days' },
  { value: '90d', short: '90D', labelKey: 'Last 90 days' },
  { value: 'month', short: 'This month', labelKey: 'This month' },
];

const isoToDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toISO = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

/** Map a feed row's type to the marker kind the calendar understands. */
const ROW_TYPE_TO_MARKER: Partial<Record<ActivityFeedRow['type'], MarkerKind>> = {
  'meal-log': 'meal',
  'exercise-log': 'exercise',
  'emotion-log': 'emotion',
  'medication-log': 'med',
};

/** Padding on each side of the selected range when pulling activity for
 *  calendar dots. Gives the clinician one month of scroll headroom in
 *  either direction without re-fetching. 30+selected+30 stays well under
 *  the feed endpoint's 500-row cap for typical ranges. */
const MARKER_PADDING_DAYS = 30;

export const DateRangePicker = ({ value, markers, patientId }: Props) => {
  const { t } = useTranslation();
  const { range, preset, setPreset, setRange, iso } = value;

  const [open, setOpen] = useState(false);

  // Last-meal chip data only — pass `enabled: false` so the heavy queries
  // (reports, devices) stay dormant. The cheap `useLastMealDate` inside
  // the hook is always eager, so the chip renders without any wait. The
  // child `QuickSelectRange` re-calls the same hook with `enabled: true`
  // once its popover opens; TanStack Query dedupes the patient query
  // between the two callers.
  const quickSelect = useQuickSelectItems(patientId, { enabled: false });
  const lastMealDate = isoToDate(quickSelect.lastMealDate);

  const buttonLabel = useMemo(() => {
    const fromFmt = format(range.from, 'MMM d, yyyy');
    const toFmt = format(range.to, 'MMM d, yyyy');
    if (iso.from === iso.to) return fromFmt;
    return `${fromFmt} → ${toFmt}`;
  }, [range, iso]);

  /**
   * Snap to a 30-day window ending on the patient's last meal date —
   * `[lastMeal − 30d, lastMeal]`. Replaces the earlier "preserve current
   * span" behavior because clinicians almost always wanted a fresh
   * standard window anchored to the patient's most recent activity, not
   * a span-preserved rollforward from whatever they last selected.
   */
  // The 30-day window ending on the patient's last logged meal — the same
  // window useDateRange applies as the first-visit smart default.
  const lastMealWindow = useMemo(() => {
    if (!lastMealDate) return null;
    const from = new Date(lastMealDate);
    from.setDate(from.getDate() - 29);
    return { from, to: lastMealDate };
  }, [lastMealDate]);

  const onSnapToLastMeal = () => {
    if (!lastMealWindow) return;
    setRange({ from: lastMealWindow.from, to: lastMealWindow.to });
  };

  // Active whenever the current range IS that window — however it got there
  // (this segment, or the first-visit smart default in useDateRange).
  const lastMealActive =
    !!lastMealWindow &&
    toISO(lastMealWindow.from) === iso.from &&
    toISO(lastMealWindow.to) === iso.to;

  const formattedLastMeal = useMemo(
    () => (lastMealDate ? format(lastMealDate, 'MMM d, yyyy') : null),
    [lastMealDate],
  );
  /**
   * Spell the window out. "Last meal" alone did not say what it selects, and
   * the dates it snaps to are not the patient's last meal date — they are the
   * 30 days ENDING on it. Name both the rule and the resolved range so the
   * clinician can see which days they are about to look at.
   */
  const lastMealTooltip = useMemo(() => {
    if (!lastMealWindow || !formattedLastMeal) return t('Last meal');
    return t('30 days ending on the last logged meal: {{from}} to {{to}}', {
      from: format(lastMealWindow.from, 'MMM d, yyyy'),
      to: formattedLastMeal,
    });
  }, [lastMealWindow, formattedLastMeal, t]);

  // Auto-derive markers from the activity feed when the caller hasn't
  // supplied them explicitly. PRE-fetch (no `open` gate) so the dots are
  // already in the cache by the time the clinician clicks the trigger —
  // previously the fetch fired on popover-open, which left the first
  // open of every new patient/range showing a dotless calendar until
  // the request resolved ~200 ms later. TanStack Query dedupes against
  // any other consumer of `useActivityFeed(patientId, sameRange)`, so
  // pages that already use that hook (Logs, Chronological Record) cost
  // zero extra requests.
  //
  // Fetch range tracks the SELECTED range (± a month of scroll padding)
  // rather than a fixed last-60-days window — historical selections
  // (e.g. a Q1 report) used to land outside the lookback window, and
  // the calendar would render dotless even though the data existed.
  const markersFetchRange = useMemo(() => {
    const from = new Date(iso.from + 'T00:00:00');
    const to = new Date(iso.to + 'T00:00:00');
    from.setDate(from.getDate() - MARKER_PADDING_DAYS);
    to.setDate(to.getDate() + MARKER_PADDING_DAYS);
    return { from: toISO(from), to: toISO(to) };
  }, [iso.from, iso.to]);
  const shouldFetchMarkers = !markers && patientId !== undefined && patientId !== null;
  const feed = useActivityFeed(shouldFetchMarkers ? (patientId as number) : 0, markersFetchRange);
  const derivedMarkers = useMemo<Partial<Record<MarkerKind, Date[]>> | undefined>(() => {
    if (!shouldFetchMarkers || !feed.data) return undefined;
    const out: Record<MarkerKind, Date[]> = {
      meal: [],
      exercise: [],
      emotion: [],
      med: [],
    };
    // Dedupe per (date, marker) — the calendar only cares whether the day
    // has ANY entry of a given type, not how many; pushing the same date
    // repeatedly bloats the modifiers array for no visual gain.
    const seen = new Set<string>();
    for (const row of feed.data.rows) {
      const kind = ROW_TYPE_TO_MARKER[row.type];
      if (!kind) continue;
      const date = row.timestamp.slice(0, 10);
      const key = `${kind}:${date}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const d = new Date(`${date}T00:00:00`);
      if (Number.isNaN(d.getTime())) continue;
      out[kind].push(d);
    }
    return out;
  }, [shouldFetchMarkers, feed.data]);

  const effectiveMarkers = markers ?? derivedMarkers;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="date-range-picker">
      {/* Preset range — a single segmented pill (7D / 30D / 90D / Last meal /
          This month) with the active preset highlighted. "Last meal" snaps to
          the 30-day window ending on the patient's last logged meal and lights
          up whenever the range matches it (including the first-visit default). */}
      <div className="inline-flex items-center rounded-full border p-0.5 text-xs">
        {PRESETS.map((p) => {
          const active = preset === p.value;
          const segClass = (on: boolean) =>
            cn(
              'rounded-full px-2.5 py-1 font-medium transition-colors',
              on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
            );
          const seg = (
            <button
              key={p.value}
              type="button"
              onClick={() => setPreset(p.value)}
              title={t(p.labelKey)}
              aria-pressed={active}
              className={segClass(active)}
            >
              {p.value === 'month' ? t(p.labelKey) : p.short}
            </button>
          );
          // Slot "Last meal" in just before "This month".
          if (p.value === 'month' && lastMealWindow) {
            return (
              <Fragment key="lastmeal-month">
                <button
                  type="button"
                  onClick={onSnapToLastMeal}
                  title={lastMealTooltip}
                  aria-pressed={lastMealActive}
                  className={segClass(lastMealActive)}
                >
                  {t('Last meal')}
                </button>
                {seg}
              </Fragment>
            );
          }
          return seg;
        })}
      </div>

      {/* Calendar trigger + Quick-select dropdown are wrapped in their
          own flex group so they wrap together as a unit when the outer
          row runs out of horizontal space — the two are functionally
          paired (both pick a range) and keeping them adjacent on the
          next line reads better than letting them split apart. */}
      <div className="flex flex-nowrap items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <CalendarDays className="mr-2 h-4 w-4" />
              {buttonLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" collisionPadding={8}>
            <MarkedRangeCalendar
              value={{ from: range.from, to: range.to }}
              onChange={(next) => {
                // Close once the user has picked both ends. `MarkedRangeCalendar`
                // emits an interim `{from, to: from}` after the first click — we
                // wait for `to !== from` before committing + closing so a
                // single-click selection doesn't lock the popover to one day.
                setRange(next);
                if (next.from.getTime() !== next.to.getTime()) {
                  setOpen(false);
                }
              }}
              markers={effectiveMarkers}
            />
          </PopoverContent>
        </Popover>

        {patientId !== undefined && patientId !== null && (
          <QuickSelectRange
            patientId={patientId}
            onPick={({ from, to }) => {
              const f = isoToDate(from);
              const t2 = isoToDate(to);
              if (!f || !t2) return;
              setRange({ from: f, to: t2 });
            }}
          />
        )}
      </div>
    </div>
  );
};
